import React, { Fragment, useCallback, useEffect, useState } from "react";
import Button from "components/form/Button";
import { clearSlice, decode, encode } from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  updateTokenExpiredStatus,
  updateTokenStatus,
  useUserData,
} from "store/slices/authSlice";
import { RootState } from "store/store";
import { useTid } from "utils/testid";

const SessionExpireModal = () => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const { clusterId: cluster_id, id } = useParams();
  const userData = useUserData();
  const navigate = useNavigate();
  const [currentSecond, setCurrentSecond] = useState(30);
  const { isExpired, isTokenRefreshing } = useAppSelector(
    (state: RootState) => state.auth
  );

  const handleLogOut = useCallback(() => {
    clearSlice(dispatch);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    let clusterId = cluster_id
      ? cluster_id
      : userData?.data?.accountData?.clusterid
        ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
        : id
          ? encode(decode(id || "")?.split("_")[1], "ci_")
          : "";
    dispatch(updateTokenExpiredStatus(false));
    dispatch(updateTokenStatus(false));
    localStorage.clear();
    navigate(`/${clusterId}/login`, { replace: true });
  }, [cluster_id, dispatch, id, navigate, userData?.data?.accountData?.clusterid]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    if (isExpired) {
      setCurrentSecond(30);
      intervalId = setInterval(() => {
        setCurrentSecond((state) => {
          if (state > 0) {
            return state - 1;
          } else {
            handleLogOut();
            return 0;
          }
        });
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [handleLogOut, isExpired]);

  return (
    <Fragment>
      <div
        className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]"
        data-testid={tid("session-expired-message")}
        aria-label={`Session expired. Returning to login in ${currentSecond} seconds`}
        aria-live="polite"
      >
        We noticed that you've been inactive for some time. For your account's
        security, we have logged you out. You can easily continue where you left
        off by clicking here to return to the customer portal.
      </div>
      <div className="w-full flex justify-end items-center lg:pt-[30px] pt-5">
        <Button
          text="Return to customer portal"
          isLoading={isTokenRefreshing}
          data-testid={tid("return-to-portal-button")}
          aria-label="Return to customer portal"
          onClick={() => {
            handleLogOut();
          }}
          className="lg:w-fit w-full"
        />
      </div>
    </Fragment>
  );
};

export default SessionExpireModal;
